import { assert } from 'ts-essentials';
import isUndefined from 'lodash/isUndefined';
import { MutationType } from '@tencent/slide-sdk-pb-model';
import {
    type IValidMutationData,
    type IValidAddAnimationMutationData,
    type IValidSetAnimationPropertiesMutationData,
} from '@tencent/slide-sdk-model';
import { K_DEFAULT_TRIGGER_ID, MEDIA_TRIGGER_ID_PREFIX } from '../constants/timing-constants';
import { UnitTimeNode } from '../model/unit-time-node';
import type { IEGTimeNodeChoice } from '@tencent/slide-sdk-pb-model';
import type { ITimingData } from '../data';
import type { KeyframeContext } from './keyframe-context.interface';
import type { MarkRequired } from 'ts-essentials';

export function getTimingKeyframe(
    timing: ITimingData,
    context: MarkRequired<KeyframeContext, 'page'>,
): IValidMutationData[] {
    const pageId = context.idFactory(context.page.id);
    const mutationDatas: Array<IValidAddAnimationMutationData | IValidSetAnimationPropertiesMutationData> = [];
    const mediaMap: Record<string, { video?: object; audio?: object }> = {};
    Object.entries(timing.unitTimeNodeMap).forEach(([triggerId, unitTimeNodeItem]) => {
        if (triggerId.startsWith(MEDIA_TRIGGER_ID_PREFIX)) {
            const mediaData = unitTimeNodeItem?.unitTimeNodeList?.[0];
            if (mediaData) {
                // 手动替换媒体资源原始数据中的id绑定
                const cMediaNode = (mediaData.video || mediaData.audio)?.cMediaNode;
                cMediaNode?.cTn?.id && (cMediaNode.cTn.id = context.idFactory(cMediaNode.cTn.id));
                // 手动替换媒体资源原始数据中的spid绑定
                const spTgt = cMediaNode?.tgtEl?.spTgt;
                spTgt?.spid && (spTgt.spid = context.idFactory(spTgt.spid));
                mediaMap[context.idFactory(triggerId.slice(MEDIA_TRIGGER_ID_PREFIX.length))] = mediaData;
            }
            return;
        }
        assert(unitTimeNodeItem);
        const newTriggerId = triggerId === K_DEFAULT_TRIGGER_ID ? K_DEFAULT_TRIGGER_ID : context.idFactory(triggerId);
        const { unitTimeNodeList, type } = unitTimeNodeItem;
        let timeNodeDropCount = 0;
        unitTimeNodeList?.forEach((unitTimeNodeData, tnIndex) => {
            const originUnitTimeNode = new UnitTimeNode(unitTimeNodeData);
            if (originUnitTimeNode.id && timing.unitTimeNodeKeepMap?.has(originUnitTimeNode.id)) {
                unitTimeNodeData = timing.unitTimeNodeKeepMap?.get(originUnitTimeNode.id) as IEGTimeNodeChoice;
                originUnitTimeNode.setData(unitTimeNodeData);
            }
            const { shapeId, animType, presetSubtype, id } = originUnitTimeNode;
            if (!shapeId || isUndefined(animType) || isUndefined(presetSubtype)) {
                timeNodeDropCount += 1;
                return;
            }
            const newShapeId = context.idFactory(shapeId);
            const addMutation: IValidAddAnimationMutationData = {
                type: MutationType.AddAnimation,
                pageId,
                shapeId: newShapeId,
                triggerId: newTriggerId,
                seqType: type,
                index: tnIndex - timeNodeDropCount,
            };
            const unitTimeNode = new UnitTimeNode(unitTimeNodeData);
            id && unitTimeNode.setId(context.idFactory(id));
            newShapeId && unitTimeNode.setShapeId(newShapeId);
            const setMutation: IValidSetAnimationPropertiesMutationData = {
                type: MutationType.SetAnimationProperties,
                shapeId: newShapeId,
                pageId,
                triggerId: newTriggerId,
                seqType: type,
                index: tnIndex - timeNodeDropCount,
                timeNode: unitTimeNode.getData(),
            };
            mutationDatas.push(addMutation, setMutation);
        });
    });
    Object.entries(mediaMap).forEach(([shapeId, mediaData]) => {
        mutationDatas.forEach((mut) => {
            if (mut.shapeId !== shapeId || mut.type !== MutationType.SetAnimationProperties) {
                return;
            }
            if (mediaData.video) {
                (mut as IValidSetAnimationPropertiesMutationData).timeNode.video = mediaData.video;
            } else if (mediaData.audio) {
                (mut as IValidSetAnimationPropertiesMutationData).timeNode.audio = mediaData.audio;
            }
        });
    });

    return mutationDatas;
}
